import { LiveFlightEvent } from '../model/liveflight-event';
import { Injectable, NgZone } from '@angular/core';
import { ActivityActorEvent } from '../model/activityActor-event';
import { ActivityActorService } from '../services/activityActor.service';
import { DayPilot } from 'daypilot-pro-angular';
import { SettingsService } from '../services/settings.service';
import { LiveFlightService } from '../services/liveFlight.service';
import { Queue } from '../common/queue';
import {GateStatus} from "../model/GateStatus";
import {SignalRService} from "../services/signalr.service";
import { isSchedulerMouseDown } from './daypilot-update-guard';

export interface ISchedulerMediatorServiceClient {
  onLiveFlightLoaded(liveFlights: LiveFlightEvent[]): void;
  updateActivityActorEvent(activityActorEvent: ActivityActorEvent): void;
  isRowMoving?(): boolean;
  instanceName(): string;
  updateNowMarker(date: DayPilot.Date): void;
  updateTimeLine(timeLine: any[]): void;

  updateLiveFlightEvents(liveFlights: LiveFlightEvent[]): void;
  deleteLiveFlightEvents(liveFlights: LiveFlightEvent[]): void;

  delayActionsDueToDropOfEvent(): void;
}

@Injectable()
export class SchedulerMediatorService {
  private updateLiveFlightsEventQueue: Queue<LiveFlightEvent[]>;
  private deleteLiveFlightEventQueue: Queue<LiveFlightEvent[]>;


  private _activityActorEvent: ActivityActorEvent[] = [];
  private clients: ISchedulerMediatorServiceClient[] = [];
  private currentDateMarker: DayPilot.Date = new DayPilot.Date;
  private isCurrentDateMarkerDirty: boolean = false;
  private isEventMoving: boolean = false;
  private startDate: DayPilot.Date;

  private currentlyUpdating: boolean = false;

  private _currentTimeLine: any[];
  private isCurrentTimeLineDirty: boolean = false;


  get currentTimeLine(): any[] { return this._currentTimeLine; }

  get liveFlights(): LiveFlightEvent[] {
    return this.liveFlightService.liveFlightList;
  }

  constructor(
    private liveFlightService: LiveFlightService,
    private activityActorService: ActivityActorService,
    private settings: SettingsService,
    private zone: NgZone,
    private signalRService: SignalRService) {

    this.updateLiveFlightsEventQueue = new Queue<LiveFlightEvent[]>();
    this.deleteLiveFlightEventQueue = new Queue<LiveFlightEvent[]>();

    let self = this;

    this.liveFlightService.liveFlightsLoaded$.subscribe((liveFlights: LiveFlightEvent[]) => {
      self.onLiveFlightLoaded(liveFlights);
      console.log("OnLiveFlightLoaded");
    });

    this.liveFlightService.updateLiveFlights$.subscribe((liveFlightEvents: any[]) => {
      this.updateLiveFlightsEventQueue.push(liveFlightEvents);
    });

    this.liveFlightService.deleteLiveFlight$.subscribe((liveFlightEvent: any[]) => {
      this.deleteLiveFlightEventQueue.push(liveFlightEvent);
    });

    this.activityActorService.updateActivity$.subscribe((activityActorEvent: ActivityActorEvent) => {
      self.onActivityActor(activityActorEvent);
    });

    setInterval(() => {
      self.isCurrentDateMarkerDirty = true;
      self.onUpdateNowMarker();
    }, 5 * 1000);

    setInterval(() => {
      self.buildTimelineAndNotify();
    }, self.settings.windowScrollInterval);


    setInterval(() => {
      self.processQueues();
    }, 200);

  }

  // Should be called upon in ngOnDestroy
  public unregisterClient(schedulerClient: ISchedulerMediatorServiceClient): void {
    let existingIndex = this.clients.findIndex(c => c.instanceName() === schedulerClient.instanceName());
    if (existingIndex !== -1) {
      this.clients.splice(existingIndex, 1);
      console.log("unregistred client => " + schedulerClient.instanceName());
    }
  }

  // Should called upon in ngAfterViewInit
  public registerClient(schedulerClient: ISchedulerMediatorServiceClient): void {

    let existingClient = this.clients.find(c => c.instanceName() === schedulerClient.instanceName());
    if (existingClient === undefined) {
      this.clients.push(schedulerClient);
      console.log("registred client => " + schedulerClient.instanceName());
    }

    if (this.clients.length === 2) {
      this.buildTimelineAndNotify();
      this.registerScrollListeners();

      // when both clients are registred check if liveflight is loaded and fire event. LiveFlightLoaded event might have been fired before the constructor
      // hooks up to loaded event
      if (this.liveFlightService.loaded) {
        console.log("LiveFlight loaded");
        this.onLiveFlightLoaded(this.liveFlightService.liveFlightList);
      }

    }
  }

  public isMoving() {
    this.isEventMoving = true;
  }

  public eventDropped() {
    this.clients.forEach((client: ISchedulerMediatorServiceClient) => client.delayActionsDueToDropOfEvent());
  }

  public movingDone() {
    setTimeout(() => {
      this.isEventMoving = false;
      this.updateAllClients();
    }, 0);
  }

  private onActivityActor(activityActorEvent: ActivityActorEvent) {
    this._activityActorEvent.unshift(activityActorEvent);
    this.updateAllClients();
  }

  private onLiveFlightLoaded(liveFlights: LiveFlightEvent[]): void {
    this.clients.forEach((client: ISchedulerMediatorServiceClient) => client.onLiveFlightLoaded(liveFlights));
  }

  private onUpdateNowMarker(): void {
    this.currentDateMarker = DayPilot.Date.now();

    if (this.schedulersReadyForUpdate() && this.isCurrentDateMarkerDirty) {
      this.currentlyUpdating = true;
      this.isCurrentDateMarkerDirty = false;
      this.clients.forEach((client: ISchedulerMediatorServiceClient) => client.updateNowMarker(this.currentDateMarker));
    }

    this.currentlyUpdating = false;
  }

  private schedulersReadyForUpdate(): boolean {
    return !this.anyClientRowMoving() && !this.isEventMoving && !isSchedulerMouseDown() && !this.currentlyUpdating;
  }

  private updateAllClients(): void {

    if (!this.schedulersReadyForUpdate()) {
      return;
    }


    if (this.isCurrentTimeLineDirty) {
      this.currentlyUpdating = true;
      this.isCurrentTimeLineDirty = false;
      this.clients.forEach((client: ISchedulerMediatorServiceClient) => client.updateTimeLine(this._currentTimeLine));
    }
    this.currentlyUpdating = false;
  }

  private processQueues() {

    if (!this.schedulersReadyForUpdate()) return;

    // Process only ONE kind of update per tick, so DayPilot can finish rendering
    // before the next update is applied. Timers are re-checked via schedulersReadyForUpdate().
    if (this._activityActorEvent.length > 0) {
      this.processActivityUpdateQueue();
    } else if (!this.updateLiveFlightsEventQueue.isEmpty()) {
      this.processUpdateLiveFlightQueue();
    } else if (!this.deleteLiveFlightEventQueue.isEmpty()) {
      this.processDeleteLiveFlightQueue();
    }
  }

  private processActivityUpdateQueue() {
    let activityActorEvent = this._activityActorEvent.pop();
    if (activityActorEvent) {
      this.safeUpdateClients(client => client.updateActivityActorEvent(activityActorEvent));
    }
  }

  private processUpdateLiveFlightQueue() {
    let flightsEvent = this.updateLiveFlightsEventQueue.pop();
    if (flightsEvent) {
      this.safeUpdateClients(client => client.updateLiveFlightEvents(flightsEvent));
    }
  }

  private processDeleteLiveFlightQueue() {
    let flightsEvent = this.deleteLiveFlightEventQueue.pop();
    if (flightsEvent) {
      this.safeUpdateClients(client => client.deleteLiveFlightEvents(flightsEvent));
    }
  }

  private safeUpdateClients(action: (client: ISchedulerMediatorServiceClient) => void) {
    this.currentlyUpdating = true;
    try {
      this.zone.run(() => this.clients.forEach(client => {
        try {
          action(client);
        } catch (e) {
          console.error("Scheduler update failed for " + client.instanceName(), e);
        }
      }));
    } finally {
      this.currentlyUpdating = false;
    }
  }



  private anyClientRowMoving(): boolean {
    return this.clients.some(c => c.isRowMoving?.() === true);
  }

  private buildTimelineAndNotify() {
    this.startDate = new DayPilot.Date(this.settings.windowStart, true);
    this.startDate = this.startDate.addMinutes(this.settings.windowScrollStep);
    this.buildTimeline(this.startDate);
    this.updateAllClients();
  }

  private buildTimeline(start: DayPilot.Date) {
    let timeline: any[] = [];

    for (let i = 0; i < this.settings.windowLength; i++) {
      start = new DayPilot.Date(start).addHours(i == 0 ? 0 : 1);
      for (let j = 0; j < 6; j++) {
        let cell = { 'start': null, 'end': null };
        cell.start = start.addMinutes(j * 10);
        cell.end = cell.start.addMinutes(10);
        timeline.push(cell);
      }
    }

    this._currentTimeLine = timeline;
    this.isCurrentTimeLineDirty = true;
  }

  private registerScrollListeners(): void {
    let dp1 = document.getElementsByClassName("scheduler_default_scrollable")[0];
    let dp2 = document.getElementsByClassName("scheduler_default_scrollable")[1];
    dp1.addEventListener('scroll', this.scrolldp1, true);
    dp2.addEventListener('scroll', this.scrolldp2, true);
  }

  // Scroll functions to sync the two daypilot views so they scroll the same amount
  private scrolldp1 = (): void => {
    let dp1 = document.getElementsByClassName("scheduler_default_scrollable")[0];
    let dp2 = document.getElementsByClassName("scheduler_default_scrollable")[1];

    if (dp1 && dp2) {
      dp2.scrollLeft = dp1.scrollLeft;
    }
  }

  private scrolldp2 = (): void => {
    let dp1 = document.getElementsByClassName("scheduler_default_scrollable")[0];
    let dp2 = document.getElementsByClassName("scheduler_default_scrollable")[1];
    if (dp1 && dp2) {
      dp1.scrollLeft = dp2.scrollLeft;
    }
  }

}
